import { Component } from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AuthorityTypeDto,
    DO_NOTHING,
    FEATURE_GENERIC_TASKS,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasFeatureFlag,
    LoggedInUser,
    OrganisationAuthorityDto,
    OrganisationAuthorityService,
    PersonsOrganisationDto,
    PreferencesService,
    UserService,
    UserStatus,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    applyPreferences,
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDatePipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { AuthorityDialoguesService } from 'libs/authorities/src/lib/authority-dialogues.service';
import { EMPTY, firstValueFrom, Subject } from 'rxjs';
import { catchError, filter, takeUntil, tap } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../../outstanding-tasks-view.service';
import { formatAuthorityExpiryWidgetStatuses } from '../../organisation/authority-data.service';
import {
    PersonalAuthorityDataService,
    PersonalTableAuthority
} from './personal-authority-data.service';

const PERSONAL_AUTHORITY_EXPIRY = 'personalAuthoritiesExpiryWidget';

/**
 * This is a generic boilerplate for the task widgets that can be used when creating new widgets.
 */
@Component({
    selector: 'personal-authority-expiries-widget',
    templateUrl: './personal-authority-expiries-widget.component.html',
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()],
    providers: [PersonalAuthorityDataService]
})
export class PersonalAuthoritiesExpiryWidgetComponent {
    organisation: PersonsOrganisationDto;
    personalOrganisationId: number;
    showBody = true;

    // Replace below with the data to display in the table
    authorities: PersonalTableAuthority[];
    formatStatuses = formatAuthorityExpiryWidgetStatuses;

    //Static table variables
    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    columnSorting: ColumnSortPreferences;
    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private workspaceStateService: WorkspaceStateService,
        private userService: UserService,
        private personalAuthorityDataService: PersonalAuthorityDataService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private preferencesService: PreferencesService,
        private authorityDialoguesService: AuthorityDialoguesService,
        private commonDialoguesService: CommonDialoguesService,
        private formatDatePipe: FormatDatePipe,
        private logging: FlyFreelyLoggingService
    ) {
        this.availableColumns = [
            {
                value: 'name',
                name: 'Authority',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                linkFunction: row => this.viewAuthority(row)
            },
            {
                value: 'identifier',
                name: 'Identifier',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'startDate',
                name: 'Start Date',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: d => this.formatDatePipe.transform(d)
            },
            {
                value: 'expiryDate',
                name: 'Expiry Date',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'status',
                name: 'Status',
                searchable: false,
                selectable: false,
                defaultSelection: true
            }
        ];

        this.selectedColumns = [];

        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.userService.userChange$
            .pipe(
                filter(
                    user => user != null && user.type === UserStatus.LOGGED_IN
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(
                (user: LoggedInUser) =>
                    (this.personalOrganisationId =
                        user.currentUser.personalOrganisationId)
            );
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.loadPreferences();
            });

        this.personalAuthorityDataService.authorities$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(authorities => (this.authorities = authorities));
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }

        this.tableConfig = {
            timeZone: this.organisation.timeZone,
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                'personalAuthorityExpiriesWidgetTable',
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.handleDefaultPreferences();
                    return EMPTY;
                })
            )
            .subscribe(preferences => {
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
            });
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3; // reset limit to default
        this.applyPreferences({});
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.columnSorting = this.userPreferences.columnSorting;
        this.tableConfig.limit = this.userPreferences.itemLimit;
        this.showBody = this.userPreferences.showBody ?? true;
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.columnSorting != null &&
            !this.selectedColumns.includes(this.columnSorting.column)
        ) {
            this.columnSorting = null;
        }
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.columnSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.columnSorting = sorting;
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'personalAuthorityExpiriesWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    viewAuthority(authority: PersonalTableAuthority) {
        const modal = this.authorityDialoguesService.showAuthorityDetails(
            this.personalOrganisationId,
            authority.authorityType,
            authority.authority,
            null,
            true,
            false,
            null
        );
        modal.content.onEditAuthority
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(payload => {
                this.editAuthority(
                    <AuthorityTypeDto>payload.authorityType,
                    payload.authority
                );
            });
        modal.content.archiveAuthority
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(payload => {
                this.archiveAuthority(
                    <AuthorityTypeDto>payload.authorityType,
                    payload.authority
                );
            });
        modal.content.unarchiveAuthority
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(payload => {
                this.unarchiveAuthority(
                    <AuthorityTypeDto>payload.authorityType,
                    payload.authority
                );
            });
    }

    editAuthority(authorityType: AuthorityTypeDto, authority: any) {
        this.authorityDialoguesService.showAuthorityEdit(
            this.personalOrganisationId,
            authorityType,
            authority,
            this.personalOrganisationId,
            this.organisationAuthorityService
        );
    }

    archiveAuthority(
        authorityType: AuthorityTypeDto,
        authority: OrganisationAuthorityDto
    ) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Archive Authority',
                `Do you wish to archive this ${authorityType.name}?`,
                'Archive',
                () =>
                    firstValueFrom(
                        this.organisationAuthorityService
                            .archiveAuthority(
                                authority.id,
                                this.organisation.id
                            )
                            .pipe(
                                tap({
                                    error: (error: FlyFreelyError) =>
                                        this.logging.error(
                                            error,
                                            `Error archiving authority: ${error.message}`
                                        )
                                })
                            )
                    )
            )
            .catch(DO_NOTHING);
    }

    unarchiveAuthority(
        authorityType: AuthorityTypeDto,
        authority: OrganisationAuthorityDto
    ) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Unarchive Authority',
                `Do you wish to unarchive this ${authorityType.name}?`,
                'Unarchive',
                () =>
                    firstValueFrom(
                        this.organisationAuthorityService
                            .unarchiveAuthority(
                                authority.id,
                                this.organisation.id
                            )
                            .pipe(
                                tap({
                                    error: (error: FlyFreelyError) =>
                                        this.logging.error(
                                            error,
                                            `Error unarchiving authority: ${error.message}`
                                        )
                                })
                            )
                    )
            )
            .catch(DO_NOTHING);
    }
}

export const PersonalAuthoritiesExpiryTaskWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: PERSONAL_AUTHORITY_EXPIRY,
        section: 'profile',
        component: PersonalAuthoritiesExpiryWidgetComponent,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_GENERIC_TASKS),
        requiredPermissions: [
            PersonsOrganisationDto.Permissions.ORGANISATION_VIEW
        ]
    };
